import React, { useEffect } from 'react';

const EventualConsistency = () => {
  useEffect(() => {
    // SEO optimization
    document.title = 'Eventual Consistency - Distributed Systems Trade-offs 2024';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Master eventual consistency in distributed systems. Learn consistency models, implementation patterns, and trade-offs for scalable architectures.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Master eventual consistency in distributed systems. Learn consistency models, implementation patterns, and trade-offs for scalable architectures.';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    return () => {
      document.title = 'SlazySloth - Deep Dives';
    };
  }, []);

  const styles = {
    container: {
      width: '100%',
      margin: 0,
      padding: '2rem',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
      lineHeight: 1.6,
      color: '#333',
      background: '#ffffff',
      fontSize: '16px'
    },
    header: {
      marginBottom: '2rem'
    },
    title: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      marginBottom: '1rem'
    },
    subtitle: {
      fontSize: '16px',
      color: '#333',
      marginBottom: '1rem',
      fontWeight: 400
    },
    content: {
      fontSize: '16px',
      lineHeight: 1.6
    },
    heading: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      margin: '1.5rem 0 1rem 0'
    },
    paragraph: {
      marginBottom: '1rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    list: {
      marginBottom: '1rem',
      paddingLeft: '1.5rem'
    },
    listItem: {
      marginBottom: '0.5rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    codeBlock: {
      background: '#f5f5f5',
      padding: '1rem',
      fontFamily: 'monospace',
      fontSize: '14px',
      color: '#333',
      marginBottom: '1rem',
      whiteSpace: 'pre-wrap',
      overflow: 'auto'
    }
  };

  return (
    <article style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.title}>Eventual Consistency: Distributed Systems Trade-offs</h1>
        <p style={styles.subtitle}>
          Balance consistency, availability, and partition tolerance in distributed architectures
        </p>
      </header>

      <main style={styles.content}>
        <section>
          <h2 style={styles.heading}>Introduction to Eventual Consistency</h2>
          <p style={styles.paragraph}>
            Eventual consistency is a consistency model used in distributed systems that guarantees 
            that, given enough time without new updates, all replicas will eventually converge to 
            the same value. Unlike strong consistency, which requires immediate consistency across 
            all nodes, eventual consistency allows temporary inconsistencies in exchange for higher 
            availability and partition tolerance.
          </p>
          <p style={styles.paragraph}>
            This model is fundamental to building scalable distributed systems, particularly those 
            that need to operate across geographic regions or handle high volumes of concurrent 
            operations. Understanding eventual consistency is crucial for designing systems that 
            can gracefully handle network partitions while maintaining acceptable user experiences.
          </p>
        </section>

        <section>
          <h2 style={styles.heading}>Consistency Models Spectrum</h2>
          
          <h3 style={styles.heading}>Understanding Consistency Levels</h3>
          <p style={styles.paragraph}>
            Distributed systems offer various consistency models, each with different trade-offs 
            between consistency guarantees and system performance. Understanding this spectrum 
            helps in choosing the right consistency model for specific use cases.
          </p>
          <pre style={styles.codeBlock}>
{`Consistency Models Spectrum:

Strong Consistency (Linearizability):
┌─────────────────────────────────────────────────────┐
│ All reads receive the most recent write             │
│ Operations appear to execute atomically             │
│ Highest consistency, lowest availability            │
└─────────────────────────────────────────────────────┘

Sequential Consistency:
┌─────────────────────────────────────────────────────┐
│ All processes see operations in same order          │
│ Operations may not reflect real-time ordering       │
│ Weaker than linearizability                         │
└─────────────────────────────────────────────────────┘

Causal Consistency:
┌─────────────────────────────────────────────────────┐
│ Causally related operations seen in same order      │
│ Concurrent operations can be seen differently       │
│ Preserves cause-and-effect relationships            │
└─────────────────────────────────────────────────────┘

Eventual Consistency:
┌─────────────────────────────────────────────────────┐
│ All replicas converge to same value eventually      │
│ No guarantees about when convergence occurs         │
│ Highest availability, weakest consistency           │
└─────────────────────────────────────────────────────┘

Consistency vs Availability Trade-off:

Strong Consistency:
✅ Immediate consistency across all nodes
✅ Simple application logic
❌ Higher latency
❌ Lower availability during partitions
❌ Limited scalability

Eventual Consistency:
✅ High availability
✅ Low latency
✅ Better scalability
✅ Partition tolerance
❌ Complex application logic
❌ Temporary inconsistencies
❌ Conflict resolution needed

Real-World Examples:

Strong Consistency:
- Bank account balances
- Inventory management
- Financial transactions
- Booking systems

Eventual Consistency:
- Social media feeds
- DNS propagation
- Content delivery networks
- Shopping cart contents
- User profiles and preferences

Consistency Model Timeline:

Time: T0    T1    T2    T3    T4    T5
Node A: [Write X=1] → [X=1] → [X=1] → [X=1] → [X=1]
Node B: [X=0] → [X=0] → [Sync] → [X=1] → [X=1]
Node C: [X=0] → [X=0] → [X=0] → [Sync] → [X=1]

Strong Consistency: All nodes show X=1 immediately at T0
Eventual Consistency: Nodes converge to X=1 by T5`}
          </pre>

          <h3 style={styles.heading}>CAP Theorem and Eventual Consistency</h3>
          <p style={styles.paragraph}>
            The CAP theorem states that distributed systems can guarantee at most two of three 
            properties: Consistency, Availability, and Partition tolerance. Eventual consistency 
            represents a choice to prioritize Availability and Partition tolerance over immediate 
            Consistency.
          </p>
          <pre style={styles.codeBlock}>
{`CAP Theorem and Eventual Consistency:

CAP Theorem Triangle:
        Consistency (C)
           /        \
          /          \
         /            \
        /              \
   (AP) \              / (CP)
         \            /
          \          /
           \        /
    Availability (A) ---- Partition Tolerance (P)

System Classifications:

CP Systems (Consistency + Partition Tolerance):
- Traditional RDBMS with strict ACID
- HBase, MongoDB (with strong consistency)
- Redis Cluster (with wait commands)
- Sacrifice availability during network partitions

AP Systems (Availability + Partition Tolerance):
- DNS
- Cassandra
- DynamoDB
- CouchDB
- Eventual consistency model

CA Systems (Consistency + Availability):
- Single-node databases
- Not suitable for distributed environments
- Network partitions break this model

Eventual Consistency in AP Systems:

Network Partition Scenario:
┌─────────────┐    Network    ┌─────────────┐
│   Node A    │    Split     │   Node B    │
│             │ ════════════ │             │
│  Write X=1  │              │  Read X=0   │
│             │              │             │
└─────────────┘              └─────────────┘

During Partition:
- Node A accepts write (X=1)
- Node B serves read (X=0) - stale data
- System remains available
- Temporary inconsistency exists

After Partition Heals:
┌─────────────┐              ┌─────────────┐
│   Node A    │ ────────────▶│   Node B    │
│    X=1      │   Sync       │    X=1      │
│             │              │             │
└─────────────┘              └─────────────┘

- Nodes synchronize
- Eventual consistency achieved
- System converges to X=1

Benefits of Choosing AP (Eventual Consistency):
✅ System remains operational during network issues
✅ Better user experience (no service unavailable errors)
✅ Higher throughput and lower latency
✅ Natural geographic distribution support
✅ Graceful degradation under load

Challenges:
❌ Application must handle inconsistent reads
❌ Conflict resolution mechanisms needed
❌ More complex business logic
❌ User confusion from temporary inconsistencies`}
          </pre>
        </section>

        <section>
          <h2 style={styles.heading}>Implementation Patterns</h2>
          
          <h3 style={styles.heading}>Vector Clocks and Conflict Resolution</h3>
          <p style={styles.paragraph}>
            Vector clocks are a mechanism for tracking causality and detecting conflicts in 
            distributed systems. They help determine the ordering of events and identify when 
            conflicts need to be resolved.
          </p>
          <pre style={styles.codeBlock}>
{`Vector Clocks Implementation:

import time
from typing import Dict, Any, Optional, List
from copy import deepcopy

class VectorClock:
    def __init__(self, node_id: str, initial_clock: Dict[str, int] = None):
        self.node_id = node_id
        self.clock = initial_clock or {node_id: 0}
    
    def tick(self):
        """Increment local clock"""
        self.clock[self.node_id] = self.clock.get(self.node_id, 0) + 1
    
    def update(self, other_clock: Dict[str, int]):
        """Update clock with received clock"""
        for node, timestamp in other_clock.items():
            self.clock[node] = max(self.clock.get(node, 0), timestamp)
        
        # Increment local clock
        self.tick()
    
    def compare(self, other_clock: Dict[str, int]) -> str:
        """Compare two vector clocks"""
        self_dominates = False
        other_dominates = False
        
        all_nodes = set(self.clock.keys()) | set(other_clock.keys())
        
        for node in all_nodes:
            self_time = self.clock.get(node, 0)
            other_time = other_clock.get(node, 0)
            
            if self_time > other_time:
                self_dominates = True
            elif self_time < other_time:
                other_dominates = True
        
        if self_dominates and not other_dominates:
            return "dominates"  # self happened after other
        elif other_dominates and not self_dominates:
            return "dominated"  # other happened after self
        elif not self_dominates and not other_dominates:
            return "equal"      # concurrent events
        else:
            return "concurrent" # conflicting concurrent events
    
    def copy(self):
        return VectorClock(self.node_id, deepcopy(self.clock))

class VersionedValue:
    def __init__(self, value: Any, vector_clock: VectorClock, timestamp: float = None):
        self.value = value
        self.vector_clock = vector_clock
        self.timestamp = timestamp or time.time()
    
    def __repr__(self):
        return f"VersionedValue(value={self.value}, clock={self.vector_clock.clock})"

class EventuallyConsistentStore:
    def __init__(self, node_id: str):
        self.node_id = node_id
        self.vector_clock = VectorClock(node_id)
        self.data = {}  # key -> List[VersionedValue]
        self.conflict_resolver = self._default_conflict_resolver
    
    def put(self, key: str, value: Any) -> VersionedValue:
        """Store a value with vector clock"""
        self.vector_clock.tick()
        
        versioned_value = VersionedValue(
            value=value,
            vector_clock=self.vector_clock.copy()
        )
        
        if key not in self.data:
            self.data[key] = []
        
        self.data[key].append(versioned_value)
        self._resolve_conflicts(key)
        
        return versioned_value
    
    def get(self, key: str) -> Optional[Any]:
        """Get the current value (after conflict resolution)"""
        if key not in self.data or not self.data[key]:
            return None
        
        # Return the resolved value
        return self.data[key][0].value
    
    def get_all_versions(self, key: str) -> List[VersionedValue]:
        """Get all versions of a key"""
        return self.data.get(key, [])
    
    def sync_with_peer(self, peer_data: Dict[str, List[VersionedValue]]):
        """Synchronize with peer node data"""
        for key, peer_versions in peer_data.items():
            if key not in self.data:
                self.data[key] = []
            
            # Merge versions
            for peer_version in peer_versions:
                # Update our vector clock
                self.vector_clock.update(peer_version.vector_clock.clock)
                
                # Check if we already have this version
                if not self._has_version(key, peer_version):
                    self.data[key].append(peer_version)
            
            # Resolve conflicts after merging
            self._resolve_conflicts(key)
    
    def _has_version(self, key: str, version: VersionedValue) -> bool:
        """Check if we already have a specific version"""
        for existing_version in self.data.get(key, []):
            if existing_version.vector_clock.clock == version.vector_clock.clock:
                return True
        return False
    
    def _resolve_conflicts(self, key: str):
        """Resolve conflicts for a key using vector clocks"""
        if key not in self.data or len(self.data[key]) <= 1:
            return
        
        versions = self.data[key]
        
        # Find concurrent (conflicting) versions
        concurrent_versions = []
        dominated_versions = []
        
        for i, version_a in enumerate(versions):
            is_dominated = False
            
            for j, version_b in enumerate(versions):
                if i != j:
                    comparison = version_a.vector_clock.compare(version_b.vector_clock.clock)
                    
                    if comparison == "dominated":
                        is_dominated = True
                        break
            
            if is_dominated:
                dominated_versions.append(version_a)
            else:
                concurrent_versions.append(version_a)
        
        # Remove dominated versions
        self.data[key] = concurrent_versions
        
        # If multiple concurrent versions exist, resolve conflict
        if len(concurrent_versions) > 1:
            resolved_value = self.conflict_resolver(concurrent_versions)
            
            # Create new version with updated vector clock
            self.vector_clock.tick()
            resolved_version = VersionedValue(
                value=resolved_value,
                vector_clock=self.vector_clock.copy()
            )
            
            self.data[key] = [resolved_version]
    
    def _default_conflict_resolver(self, conflicting_versions: List[VersionedValue]) -> Any:
        """Default conflict resolution: last-write-wins by timestamp"""
        return max(conflicting_versions, key=lambda v: v.timestamp).value
    
    def set_conflict_resolver(self, resolver_func):
        """Set custom conflict resolution function"""
        self.conflict_resolver = resolver_func

# Usage Example
def custom_conflict_resolver(versions):
    """Custom conflict resolver: merge string values"""
    if all(isinstance(v.value, str) for v in versions):
        return " | ".join(v.value for v in versions)
    else:
        # Fall back to last-write-wins
        return max(versions, key=lambda v: v.timestamp).value

# Create nodes
node_a = EventuallyConsistentStore("node_a")
node_b = EventuallyConsistentStore("node_b")
node_c = EventuallyConsistentStore("node_c")

# Set custom conflict resolver
node_a.set_conflict_resolver(custom_conflict_resolver)
node_b.set_conflict_resolver(custom_conflict_resolver)

# Simulate concurrent writes
print("=== Concurrent Writes ===")
node_a.put("user_status", "online")
node_b.put("user_status", "busy")

print(f"Node A sees: {node_a.get('user_status')}")
print(f"Node B sees: {node_b.get('user_status')}")

# Simulate synchronization
print("\\n=== After Synchronization ===")
node_a.sync_with_peer(node_b.data)
node_b.sync_with_peer(node_a.data)

print(f"Node A sees: {node_a.get('user_status')}")
print(f"Node B sees: {node_b.get('user_status')}")

# Check all versions
print("\\n=== All Versions ===")
for version in node_a.get_all_versions("user_status"):
    print(f"Version: {version}")`}
          </pre>

          <h3 style={styles.heading}>CRDT (Conflict-free Replicated Data Types)</h3>
          <p style={styles.paragraph}>
            CRDTs are data structures that automatically resolve conflicts in a mathematically 
            provable way, ensuring eventual consistency without requiring coordination between 
            replicas.
          </p>
          <pre style={styles.codeBlock}>
{`CRDT Implementation Examples:

# G-Counter (Grow-only Counter)
class GCounter:
    def __init__(self, node_id: str):
        self.node_id = node_id
        self.counters = {node_id: 0}
    
    def increment(self, amount: int = 1):
        """Increment counter locally"""
        self.counters[self.node_id] += amount
    
    def value(self) -> int:
        """Get current counter value"""
        return sum(self.counters.values())
    
    def merge(self, other: 'GCounter'):
        """Merge with another G-Counter"""
        for node_id, count in other.counters.items():
            self.counters[node_id] = max(
                self.counters.get(node_id, 0), 
                count
            )
    
    def compare(self, other: 'GCounter') -> str:
        """Compare with another G-Counter"""
        all_nodes = set(self.counters.keys()) | set(other.counters.keys())
        
        self_dominates = False
        other_dominates = False
        
        for node in all_nodes:
            self_count = self.counters.get(node, 0)
            other_count = other.counters.get(node, 0)
            
            if self_count > other_count:
                self_dominates = True
            elif self_count < other_count:
                other_dominates = True
        
        if self_dominates and not other_dominates:
            return "dominates"
        elif other_dominates and not self_dominates:
            return "dominated"
        else:
            return "concurrent"

# PN-Counter (Increment/Decrement Counter)
class PNCounter:
    def __init__(self, node_id: str):
        self.node_id = node_id
        self.increments = GCounter(node_id)
        self.decrements = GCounter(node_id)
    
    def increment(self, amount: int = 1):
        self.increments.increment(amount)
    
    def decrement(self, amount: int = 1):
        self.decrements.increment(amount)
    
    def value(self) -> int:
        return self.increments.value() - self.decrements.value()
    
    def merge(self, other: 'PNCounter'):
        self.increments.merge(other.increments)
        self.decrements.merge(other.decrements)

# G-Set (Grow-only Set)
class GSet:
    def __init__(self):
        self.elements = set()
    
    def add(self, element):
        """Add element to set"""
        self.elements.add(element)
    
    def contains(self, element) -> bool:
        """Check if element is in set"""
        return element in self.elements
    
    def merge(self, other: 'GSet'):
        """Merge with another G-Set"""
        self.elements.update(other.elements)
    
    def __iter__(self):
        return iter(self.elements)

# OR-Set (Observed-Remove Set)
class ORSet:
    def __init__(self, node_id: str):
        self.node_id = node_id
        self.added = {}      # element -> set of unique tags
        self.removed = set() # set of removed tags
        self.tag_counter = 0
    
    def add(self, element):
        """Add element with unique tag"""
        self.tag_counter += 1
        tag = f"{self.node_id}_{self.tag_counter}"
        
        if element not in self.added:
            self.added[element] = set()
        
        self.added[element].add(tag)
    
    def remove(self, element):
        """Remove element by marking all its tags as removed"""
        if element in self.added:
            self.removed.update(self.added[element])
    
    def contains(self, element) -> bool:
        """Check if element is in set"""
        if element not in self.added:
            return False
        
        # Element is present if it has tags that aren't removed
        return bool(self.added[element] - self.removed)
    
    def elements(self) -> set:
        """Get all elements in the set"""
        result = set()
        for element, tags in self.added.items():
            if tags - self.removed:  # Has non-removed tags
                result.add(element)
        return result
    
    def merge(self, other: 'ORSet'):
        """Merge with another OR-Set"""
        # Merge added elements
        for element, tags in other.added.items():
            if element not in self.added:
                self.added[element] = set()
            self.added[element].update(tags)
        
        # Merge removed tags
        self.removed.update(other.removed)

# LWW-Register (Last-Write-Wins Register)
class LWWRegister:
    def __init__(self, node_id: str):
        self.node_id = node_id
        self.value = None
        self.timestamp = 0
        self.node_id_tiebreaker = node_id
    
    def set(self, value, timestamp: float = None):
        """Set value with timestamp"""
        if timestamp is None:
            timestamp = time.time()
        
        if (timestamp > self.timestamp or 
            (timestamp == self.timestamp and self.node_id > self.node_id_tiebreaker)):
            self.value = value
            self.timestamp = timestamp
            self.node_id_tiebreaker = self.node_id
    
    def get(self):
        """Get current value"""
        return self.value
    
    def merge(self, other: 'LWWRegister'):
        """Merge with another LWW-Register"""
        if (other.timestamp > self.timestamp or
            (other.timestamp == self.timestamp and other.node_id_tiebreaker > self.node_id_tiebreaker)):
            self.value = other.value
            self.timestamp = other.timestamp
            self.node_id_tiebreaker = other.node_id_tiebreaker

# Usage Examples
print("=== G-Counter Example ===")
counter_a = GCounter("node_a")
counter_b = GCounter("node_b")

counter_a.increment(5)
counter_b.increment(3)

print(f"Counter A: {counter_a.value()}")  # 5
print(f"Counter B: {counter_b.value()}")  # 3

# Merge counters
counter_a.merge(counter_b)
counter_b.merge(counter_a)

print(f"After merge - Counter A: {counter_a.value()}")  # 8
print(f"After merge - Counter B: {counter_b.value()}")  # 8

print("\\n=== OR-Set Example ===")
set_a = ORSet("node_a")
set_b = ORSet("node_b")

set_a.add("apple")
set_a.add("banana")
set_b.add("apple")
set_b.add("cherry")

print(f"Set A: {set_a.elements()}")
print(f"Set B: {set_b.elements()}")

# Concurrent remove and add
set_a.remove("apple")  # Node A removes apple
set_b.add("apple")     # Node B adds apple again (different tag)

# Merge sets
set_a.merge(set_b)
set_b.merge(set_a)

print(f"After merge - Set A: {set_a.elements()}")
print(f"After merge - Set B: {set_b.elements()}")

print("\\n=== LWW-Register Example ===")
reg_a = LWWRegister("node_a")
reg_b = LWWRegister("node_b")

reg_a.set("value_from_a", 1000)
reg_b.set("value_from_b", 1001)  # Later timestamp

print(f"Register A: {reg_a.get()}")
print(f"Register B: {reg_b.get()}")

# Merge registers
reg_a.merge(reg_b)
reg_b.merge(reg_a)

print(f"After merge - Register A: {reg_a.get()}")  # value_from_b (later timestamp)
print(f"After merge - Register B: {reg_b.get()}")  # value_from_b`}
          </pre>
        </section>

        <section>
          <h2 style={styles.heading}>Real-World Applications</h2>
          
          <h3 style={styles.heading}>Distributed Database Systems</h3>
          <p style={styles.paragraph}>
            Many distributed databases implement eventual consistency to achieve high availability 
            and partition tolerance. Understanding how these systems work helps in designing 
            applications that can leverage their strengths.
          </p>
          <pre style={styles.codeBlock}>
{`Distributed Database Eventual Consistency:

# DynamoDB-style Eventually Consistent System
import hashlib
import json
import time
from typing import Dict, Any, List, Optional

class ConsistentHashRing:
    def __init__(self, nodes: List[str], replicas: int = 3):
        self.nodes = nodes
        self.replicas = replicas
        self.ring = {}
        self._build_ring()
    
    def _build_ring(self):
        """Build consistent hash ring"""
        for node in self.nodes:
            for i in range(self.replicas):
                key = self._hash(f"{node}:{i}")
                self.ring[key] = node
    
    def _hash(self, key: str) -> int:
        """Hash function for ring"""
        return int(hashlib.md5(key.encode()).hexdigest(), 16)
    
    def get_nodes(self, key: str, n: int = 3) -> List[str]:
        """Get N nodes responsible for key"""
        if not self.ring:
            return []
        
        key_hash = self._hash(key)
        
        # Find nodes in clockwise direction
        sorted_hashes = sorted(self.ring.keys())
        nodes = []
        
        # Start from the first node >= key_hash
        start_idx = 0
        for i, ring_hash in enumerate(sorted_hashes):
            if ring_hash >= key_hash:
                start_idx = i
                break
        
        # Collect unique nodes
        seen_nodes = set()
        for i in range(len(sorted_hashes)):
            idx = (start_idx + i) % len(sorted_hashes)
            node = self.ring[sorted_hashes[idx]]
            
            if node not in seen_nodes:
                nodes.append(node)
                seen_nodes.add(node)
                
                if len(nodes) >= n:
                    break
        
        return nodes

class DistributedKVStore:
    def __init__(self, node_id: str, all_nodes: List[str], replication_factor: int = 3):
        self.node_id = node_id
        self.all_nodes = all_nodes
        self.replication_factor = replication_factor
        self.hash_ring = ConsistentHashRing(all_nodes)
        
        # Local storage
        self.data = {}  # key -> {value, timestamp, vector_clock}
        self.vector_clock = VectorClock(node_id)
        
        # Peer connections (simplified)
        self.peers = {node: None for node in all_nodes if node != node_id}
    
    def put(self, key: str, value: Any, consistency_level: str = "eventual") -> bool:
        """Put key-value with consistency level"""
        # Get responsible nodes
        responsible_nodes = self.hash_ring.get_nodes(key, self.replication_factor)
        
        if self.node_id not in responsible_nodes:
            # Forward to responsible node
            return self._forward_put(responsible_nodes[0], key, value)
        
        # Update local vector clock
        self.vector_clock.tick()
        
        # Create versioned entry
        entry = {
            'value': value,
            'timestamp': time.time(),
            'vector_clock': self.vector_clock.clock.copy(),
            'node_id': self.node_id
        }
        
        # Store locally
        if key not in self.data:
            self.data[key] = []
        self.data[key].append(entry)
        
        # Replicate to other responsible nodes
        success_count = 1  # Local write succeeded
        
        for node in responsible_nodes[1:]:
            if self._replicate_to_node(node, key, entry):
                success_count += 1
        
        # Check consistency requirements
        if consistency_level == "strong":
            return success_count == len(responsible_nodes)
        elif consistency_level == "quorum":
            return success_count > len(responsible_nodes) // 2
        else:  # eventual
            return success_count >= 1
    
    def get(self, key: str, consistency_level: str = "eventual") -> Optional[Any]:
        """Get value with consistency level"""
        responsible_nodes = self.hash_ring.get_nodes(key, self.replication_factor)
        
        if consistency_level == "eventual":
            # Read from local replica if available
            if self.node_id in responsible_nodes and key in self.data:
                return self._resolve_conflicts(key)
            else:
                # Forward to responsible node
                return self._forward_get(responsible_nodes[0], key)
        
        elif consistency_level == "quorum":
            # Read from majority of replicas
            responses = []
            
            if self.node_id in responsible_nodes and key in self.data:
                responses.append(self.data[key])
            
            # Query other nodes
            for node in responsible_nodes:
                if node != self.node_id:
                    response = self._query_node(node, key)
                    if response:
                        responses.append(response)
            
            # Need majority responses
            required_responses = len(responsible_nodes) // 2 + 1
            if len(responses) >= required_responses:
                # Merge and resolve conflicts
                return self._merge_responses(responses)
            else:
                return None
    
    def _resolve_conflicts(self, key: str) -> Any:
        """Resolve conflicts using vector clocks and timestamps"""
        if key not in self.data or not self.data[key]:
            return None
        
        versions = self.data[key]
        
        if len(versions) == 1:
            return versions[0]['value']
        
        # Find non-dominated versions
        concurrent_versions = []
        
        for version_a in versions:
            is_dominated = False
            
            for version_b in versions:
                if version_a != version_b:
                    # Compare vector clocks
                    comparison = self._compare_vector_clocks(
                        version_a['vector_clock'],
                        version_b['vector_clock']
                    )
                    
                    if comparison == "dominated":
                        is_dominated = True
                        break
            
            if not is_dominated:
                concurrent_versions.append(version_a)
        
        # If still multiple versions, use timestamp
        if len(concurrent_versions) > 1:
            latest_version = max(concurrent_versions, key=lambda v: v['timestamp'])
            return latest_version['value']
        else:
            return concurrent_versions[0]['value']
    
    def _compare_vector_clocks(self, clock_a: Dict[str, int], clock_b: Dict[str, int]) -> str:
        """Compare two vector clocks"""
        all_nodes = set(clock_a.keys()) | set(clock_b.keys())
        
        a_dominates = False
        b_dominates = False
        
        for node in all_nodes:
            a_time = clock_a.get(node, 0)
            b_time = clock_b.get(node, 0)
            
            if a_time > b_time:
                a_dominates = True
            elif a_time < b_time:
                b_dominates = True
        
        if a_dominates and not b_dominates:
            return "dominates"
        elif b_dominates and not a_dominates:
            return "dominated"
        else:
            return "concurrent"
    
    def sync_with_peers(self):
        """Background sync with peer nodes"""
        for peer_node in self.peers:
            try:
                # Get peer's data
                peer_data = self._get_peer_data(peer_node)
                
                # Merge peer data
                for key, peer_versions in peer_data.items():
                    if key not in self.data:
                        self.data[key] = []
                    
                    # Add new versions
                    for peer_version in peer_versions:
                        if not self._has_version(key, peer_version):
                            self.data[key].append(peer_version)
                            
                            # Update vector clock
                            self.vector_clock.update(peer_version['vector_clock'])
                
            except Exception as e:
                print(f"Sync error with {peer_node}: {e}")
    
    def _has_version(self, key: str, version: Dict[str, Any]) -> bool:
        """Check if we already have this version"""
        for existing_version in self.data.get(key, []):
            if (existing_version['vector_clock'] == version['vector_clock'] and
                existing_version['timestamp'] == version['timestamp']):
                return True
        return False

# Usage Example
nodes = ['node_a', 'node_b', 'node_c', 'node_d', 'node_e']

# Create distributed nodes
store_a = DistributedKVStore('node_a', nodes)
store_b = DistributedKVStore('node_b', nodes)
store_c = DistributedKVStore('node_c', nodes)

# Simulate network partition
print("=== Before Network Partition ===")
store_a.put('user:123:name', 'Alice')
print(f"Node A reads: {store_a.get('user:123:name')}")

# Simulate partition - nodes can't communicate
print("\\n=== During Network Partition ===")
store_a.put('user:123:name', 'Alice Updated')  # Node A updates
store_b.put('user:123:name', 'Alice Modified')  # Node B updates concurrently

print(f"Node A reads: {store_a.get('user:123:name')}")
print(f"Node B reads: {store_b.get('user:123:name')}")

# Simulate partition healing
print("\\n=== After Partition Heals ===")
store_a.sync_with_peers()
store_b.sync_with_peers()

print(f"Node A reads: {store_a.get('user:123:name')}")
print(f"Node B reads: {store_b.get('user:123:name')}")

# Demonstrate different consistency levels
print("\\n=== Consistency Levels ===")
store_a.put('product:456:price', 100, consistency_level="eventual")
store_a.put('account:789:balance', 1000, consistency_level="strong")

print("Eventual consistency: High availability, may read stale data")
print("Strong consistency: Lower availability, always consistent reads")`}
          </pre>

          <h3 style={styles.heading}>Content Distribution Networks</h3>
          <p style={styles.paragraph}>
            CDNs are a classic example of eventual consistency in action, where content updates 
            propagate gradually across edge servers worldwide, balancing performance with consistency.
          </p>
          <pre style={styles.codeBlock}>
{`CDN Eventual Consistency Implementation:

import time
import random
from typing import Dict, Any, List, Optional
from dataclasses import dataclass
from enum import Enum

class CacheStatus(Enum):
    FRESH = "fresh"
    STALE = "stale"
    EXPIRED = "expired"

@dataclass
class CacheEntry:
    content: Any
    created_at: float
    last_modified: float
    etag: str
    ttl: int  # Time to live in seconds
    version: int = 1
    
    def is_fresh(self) -> bool:
        return time.time() - self.created_at < self.ttl
    
    def is_expired(self) -> bool:
        return time.time() - self.created_at > self.ttl * 2
    
    def get_status(self) -> CacheStatus:
        age = time.time() - self.created_at
        if age < self.ttl:
            return CacheStatus.FRESH
        elif age < self.ttl * 2:
            return CacheStatus.STALE
        else:
            return CacheStatus.EXPIRED

class CDNEdgeServer:
    def __init__(self, server_id: str, region: str):
        self.server_id = server_id
        self.region = region
        self.cache = {}  # path -> CacheEntry
        self.origin_servers = []
        self.peer_servers = []
        
        # Configuration
        self.default_ttl = 3600  # 1 hour
        self.stale_while_revalidate = True
        self.propagation_delay = random.uniform(1, 10)  # Simulate network delay
    
    def get_content(self, path: str, client_ip: str = None) -> Optional[Dict[str, Any]]:
        """Get content with eventual consistency semantics"""
        cache_entry = self.cache.get(path)
        
        if not cache_entry:
            # Cache miss - fetch from origin
            return self._fetch_from_origin(path)
        
        status = cache_entry.get_status()
        
        if status == CacheStatus.FRESH:
            # Serve fresh content
            return {
                'content': cache_entry.content,
                'source': 'cache',
                'age': time.time() - cache_entry.created_at,
                'server': self.server_id
            }
        
        elif status == CacheStatus.STALE and self.stale_while_revalidate:
            # Serve stale content while revalidating in background
            self._async_revalidate(path)
            
            return {
                'content': cache_entry.content,
                'source': 'stale_cache',
                'age': time.time() - cache_entry.created_at,
                'server': self.server_id,
                'warning': 'stale_content'
            }
        
        else:
            # Content expired - fetch fresh content
            return self._fetch_from_origin(path)
    
    def put_content(self, path: str, content: Any, ttl: int = None, 
                   etag: str = None, version: int = 1) -> bool:
        """Put content in cache"""
        if ttl is None:
            ttl = self.default_ttl
        
        if etag is None:
            etag = f"etag_{hash(str(content))}_{int(time.time())}"
        
        cache_entry = CacheEntry(
            content=content,
            created_at=time.time(),
            last_modified=time.time(),
            etag=etag,
            ttl=ttl,
            version=version
        )
        
        self.cache[path] = cache_entry
        
        # Propagate to peer servers with eventual consistency
        self._propagate_to_peers(path, cache_entry)
        
        return True
    
    def invalidate_content(self, path: str) -> bool:
        """Invalidate content across CDN"""
        if path in self.cache:
            del self.cache[path]
        
        # Propagate invalidation to peers
        self._propagate_invalidation(path)
        
        return True
    
    def _fetch_from_origin(self, path: str) -> Optional[Dict[str, Any]]:
        """Fetch content from origin server"""
        # Simulate origin fetch
        time.sleep(0.1)  # Network latency
        
        if random.random() < 0.9:  # 90% success rate
            content = f"Content for {path} from origin"
            etag = f"origin_etag_{hash(content)}_{int(time.time())}"
            
            # Cache the content
            self.put_content(path, content, etag=etag)
            
            return {
                'content': content,
                'source': 'origin',
                'age': 0,
                'server': self.server_id
            }
        else:
            # Origin unavailable - serve stale if available
            cache_entry = self.cache.get(path)
            if cache_entry:
                return {
                    'content': cache_entry.content,
                    'source': 'stale_fallback',
                    'age': time.time() - cache_entry.created_at,
                    'server': self.server_id,
                    'warning': 'origin_unavailable'
                }
            return None
    
    def _async_revalidate(self, path: str):
        """Asynchronously revalidate stale content"""
        # In real implementation, this would be done in background thread
        print(f"[{self.server_id}] Async revalidating {path}")
        
        # Simulate revalidation
        if random.random() < 0.8:  # 80% success rate
            fresh_content = self._fetch_from_origin(path)
            if fresh_content:
                print(f"[{self.server_id}] Revalidation successful for {path}")
    
    def _propagate_to_peers(self, path: str, cache_entry: CacheEntry):
        """Propagate content to peer servers (eventual consistency)"""
        for peer in self.peer_servers:
            # Simulate network delay and potential failures
            if random.random() < 0.9:  # 90% success rate
                # Delayed propagation
                delay = random.uniform(1, self.propagation_delay)
                print(f"[{self.server_id}] Propagating {path} to {peer.server_id} (delay: {delay:.1f}s)")
                
                # In real implementation, this would be async
                peer._receive_propagated_content(path, cache_entry, self.server_id)
    
    def _receive_propagated_content(self, path: str, cache_entry: CacheEntry, 
                                  source_server: str):
        """Receive propagated content from peer"""
        existing_entry = self.cache.get(path)
        
        # Only update if newer version or no existing content
        if (not existing_entry or 
            cache_entry.version > existing_entry.version or
            cache_entry.last_modified > existing_entry.last_modified):
            
            # Create local copy with adjusted timestamp
            local_entry = CacheEntry(
                content=cache_entry.content,
                created_at=time.time(),  # Local cache time
                last_modified=cache_entry.last_modified,
                etag=cache_entry.etag,
                ttl=cache_entry.ttl,
                version=cache_entry.version
            )
            
            self.cache[path] = local_entry
            print(f"[{self.server_id}] Received propagated content for {path} from {source_server}")
    
    def _propagate_invalidation(self, path: str):
        """Propagate invalidation to peer servers"""
        for peer in self.peer_servers:
            if random.random() < 0.95:  # 95% success rate for invalidation
                peer._receive_invalidation(path, self.server_id)
    
    def _receive_invalidation(self, path: str, source_server: str):
        """Receive invalidation from peer"""
        if path in self.cache:
            del self.cache[path]
            print(f"[{self.server_id}] Invalidated {path} from {source_server}")
    
    def get_cache_stats(self) -> Dict[str, Any]:
        """Get cache statistics"""
        total_entries = len(self.cache)
        fresh_entries = sum(1 for entry in self.cache.values() if entry.is_fresh())
        stale_entries = total_entries - fresh_entries
        
        return {
            'server_id': self.server_id,
            'region': self.region,
            'total_entries': total_entries,
            'fresh_entries': fresh_entries,
            'stale_entries': stale_entries,
            'cache_hit_ratio': fresh_entries / max(total_entries, 1)
        }

class CDNNetwork:
    def __init__(self):
        self.edge_servers = {}
        self.origin_servers = {}
    
    def add_edge_server(self, server_id: str, region: str) -> CDNEdgeServer:
        """Add edge server to CDN network"""
        server = CDNEdgeServer(server_id, region)
        self.edge_servers[server_id] = server
        
        # Connect to other servers in same region as peers
        for existing_server in self.edge_servers.values():
            if existing_server.region == region and existing_server != server:
                server.peer_servers.append(existing_server)
                existing_server.peer_servers.append(server)
        
        return server
    
    def update_content(self, path: str, content: Any, version: int = 1):
        """Update content at origin and propagate"""
        print(f"\\n=== Updating content: {path} (version {version}) ===")
        
        # Update at a random edge server (simulating origin update)
        if self.edge_servers:
            origin_server = random.choice(list(self.edge_servers.values()))
            origin_server.put_content(path, content, version=version)
    
    def get_content_from_region(self, path: str, region: str) -> Optional[Dict[str, Any]]:
        """Get content from specific region"""
        region_servers = [s for s in self.edge_servers.values() if s.region == region]
        
        if region_servers:
            server = random.choice(region_servers)
            return server.get_content(path)
        
        return None
    
    def simulate_eventual_consistency(self, path: str, content: Any):
        """Simulate eventual consistency propagation"""
        print(f"\\n=== Simulating Eventual Consistency for {path} ===")
        
        # Initial update
        self.update_content(path, content, version=1)
        
        # Check consistency across regions over time
        regions = list(set(s.region for s in self.edge_servers.values()))
        
        for i in range(5):  # Check 5 times over time
            print(f"\\nTime T+{i*2}s:")
            
            for region in regions:
                response = self.get_content_from_region(path, region)
                if response:
                    print(f"  {region}: {response['source']} - age: {response.get('age', 0):.1f}s")
                else:
                    print(f"  {region}: No content found")
            
            time.sleep(2)  # Wait 2 seconds
    
    def get_network_stats(self) -> Dict[str, Any]:
        """Get network-wide statistics"""
        stats = {
            'total_servers': len(self.edge_servers),
            'regions': list(set(s.region for s in self.edge_servers.values())),
            'server_stats': {}
        }
        
        for server_id, server in self.edge_servers.items():
            stats['server_stats'][server_id] = server.get_cache_stats()
        
        return stats

# Usage Example
cdn = CDNNetwork()

# Create edge servers in different regions
cdn.add_edge_server('us-east-1a', 'us-east')
cdn.add_edge_server('us-east-1b', 'us-east')
cdn.add_edge_server('us-west-1a', 'us-west')
cdn.add_edge_server('eu-west-1a', 'eu-west')
cdn.add_edge_server('ap-south-1a', 'ap-south')

# Simulate content updates and eventual consistency
cdn.simulate_eventual_consistency('/api/users/123', {'name': 'Alice', 'status': 'active'})

# Show network statistics
stats = cdn.get_network_stats()
print("\\n=== CDN Network Statistics ===")
for server_id, server_stats in stats['server_stats'].items():
    print(f"{server_id}: {server_stats['fresh_entries']} fresh, {server_stats['stale_entries']} stale")

# Demonstrate stale-while-revalidate
print("\\n=== Stale-While-Revalidate Example ===")
server = cdn.edge_servers['us-east-1a']

# Put content that will become stale
server.put_content('/api/news/latest', 'Breaking news!', ttl=1)  # 1 second TTL
time.sleep(2)  # Wait for content to become stale

# Request stale content
response = server.get_content('/api/news/latest')
print(f"Response: {response}")
print("Note: Served stale content while revalidating in background")`}
          </pre>
        </section>

        <section>
          <h2 style={styles.heading}>Best Practices</h2>
          <ul style={styles.list}>
            <li style={styles.listItem}>
              <strong>Design for eventual consistency from the start:</strong> Build your application 
              architecture with the assumption that data may be temporarily inconsistent.
            </li>
            <li style={styles.listItem}>
              <strong>Implement proper conflict resolution:</strong> Use vector clocks, CRDTs, or 
              application-specific logic to handle conflicting updates gracefully.
            </li>
            <li style={styles.listItem}>
              <strong>Provide user feedback about consistency:</strong> Inform users when they might 
              be seeing stale data or when updates are still propagating.
            </li>
            <li style={styles.listItem}>
              <strong>Choose appropriate consistency levels:</strong> Use strong consistency for 
              critical operations and eventual consistency for less critical data.
            </li>
            <li style={styles.listItem}>
              <strong>Monitor convergence times:</strong> Track how long it takes for updates to 
              propagate across your system and optimize based on requirements.
            </li>
            <li style={styles.listItem}>
              <strong>Test partition scenarios:</strong> Regularly test how your system behaves 
              during network partitions and ensure graceful degradation.
            </li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>Key Takeaways</h2>
          <p style={styles.paragraph}>
            Eventual consistency is a powerful consistency model that enables distributed systems to 
            achieve high availability and partition tolerance at the cost of immediate consistency. 
            It requires careful design of conflict resolution mechanisms and user experience considerations 
            to handle temporary inconsistencies gracefully.
          </p>
          <p style={styles.paragraph}>
            Success with eventual consistency depends on understanding the trade-offs, implementing 
            appropriate conflict resolution strategies, and designing applications that can work with 
            temporarily inconsistent data. When implemented correctly, it enables systems to scale 
            globally while maintaining acceptable user experiences.
          </p>
        </section>
      </main>
    </article>
  );
};

export default EventualConsistency;
